import React, { FC, useCallback, useState } from 'react';
import { Alert, Badge, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { api } from '../functions/api';
import { ApiPath } from '../api/ApiPath';
import { AliasPreview as Preview } from './dto/EntityAlias';

/**
 * Type a word, see what the bot does with it.
 *
 * The list below shows which aliases exist; it cannot show what a clerk saying "אשדוד"
 * would actually get, because that depends on every other row that answers to the same
 * word. This asks the resolver directly, so a change can be checked instead of assumed.
 * Nothing is written.
 */
export const AliasPreview: FC = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const run = useCallback(
    async (word: string) => {
      const trimmed = word.trim();
      if (!trimmed) return;

      setLoading(true);
      setFailed(false);
      try {
        setResult(await api<Preview>({ url: ApiPath.EntityAliasPreview, params: { q: trimmed } }));
      } catch {
        setFailed(true);
        setResult(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return (
    <div className="mb-4">
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          run(query);
        }}
      >
        <InputGroup>
          <Form.Control
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('Try a word the way a clerk would say it').toString()}
            aria-label={t('Word to try').toString()}
          />
          <Button type="submit" variant="secondary" disabled={loading || !query.trim()}>
            {loading ? <Spinner size="sm" animation="border" /> : t('Try it')}
          </Button>
        </InputGroup>
      </Form>

      {failed && (
        <Alert variant="danger" className="mt-2 mb-0">
          {t('Could not check that word')}
        </Alert>
      )}

      {result && !failed && (
        <Alert variant={result.wouldAsk ? 'warning' : result.resolvesTo ? 'success' : 'secondary'} className="mt-2 mb-0">
          {result.wouldAsk && <strong>{t('The bot would ask which one was meant')}</strong>}
          {!result.wouldAsk && result.resolvesTo && (
            <>
              <strong>{t('Resolves to')}: {result.resolvesTo.canonical}</strong>{' '}
              <Badge bg="light" text="dark">
                {result.resolvesTo.entityType}
              </Badge>
            </>
          )}
          {!result.wouldAsk && !result.resolvesTo && <strong>{t('Nothing in the catalogue answers to this word')}</strong>}

          {result.candidates.length > 0 && (
            <ul className="mb-0 mt-2">
              {result.candidates.map((candidate) => (
                <li key={`${candidate.entityType}:${candidate.entityId}`}>
                  {candidate.canonical}{' '}
                  <span className="text-muted">
                    ({candidate.entityType}, {t('score')} {candidate.score}, {candidate.via})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}
    </div>
  );
};
